import { STORAGE_KEY_PREFIX } from "@/constants";
import { inMemoryStorage } from "../utils/in-memory-storage";
import persistedStorage from "../utils/indexeddb";
import log from "../../../utils/logger";

const storage = inMemoryStorage;

/**
 * Saves a string value to storage with a prefixed key
 * @param key - The storage key (will be prefixed with STORAGE_KEY_PREFIX)
 * @param value - The string value to store
 * @returns Promise resolving to true when saved successfully
 */
export async function saveToStorage(key: string, value: string): Promise<boolean> {
  return new Promise((resolve, reject) => {
    try {
      // Prefix the key to avoid collisions with other apps using storage
      storage.setItem(`${STORAGE_KEY_PREFIX}${key}`, value);
      // Return true to indicate successful save operation
      resolve(true);

      // snapshot to persistant storage
      persistedStorage.setItem(`${STORAGE_KEY_PREFIX}${key}`, value);
    } catch (error) {
      // Log error for debugging purposes
      log.error(`Error in saveToStorage for key="${key}":`, undefined, error);
      // Reject promise with the error to allow caller to handle it
      reject(error);
    }
  });
}

/**
 * Retrieves a string value from storage using a prefixed key
 * @param key - The storage key (will be prefixed with STORAGE_KEY_PREFIX)
 * @returns Promise resolving to the stored value or undefined if not found
 */
export async function getFromStorage(key: string): Promise<string | undefined> {
  let value = undefined;
  // Get the value from storage using the prefixed key
  value = storage.getItem(`${STORAGE_KEY_PREFIX}${key}`);

  // if there is nothing in memory, try to get from persisted storage
  if (!value) {
    try {
      value = await persistedStorage.getItem(`${STORAGE_KEY_PREFIX}${key}`);
    } catch (e) {
      // Log error for debugging purposes
      log.error(`Error in getFromStorage for persistent storage for key="${key}":`, undefined, e);
    }
  }

  // Return the value if it exists, otherwise undefined
  return value || undefined;
}

/**
 * Removes a specific item from storage using a prefixed key
 * @param key - The storage key (will be prefixed with STORAGE_KEY_PREFIX)
 * @returns Promise resolving to true when removed successfully
 */
export async function removeFromStorage(key: string): Promise<boolean> {
  return new Promise((resolve, reject) => {
    try {
      // Remove the item from storage using the prefixed key
      storage.removeItem(`${STORAGE_KEY_PREFIX}${key}`);
      // Return true to indicate successful removal

      // remove from persisted storage
      try {
        persistedStorage.removeItem(`${STORAGE_KEY_PREFIX}${key}`);
      } catch (e) {
        // Log error for debugging purposes
        log.error(`Error in removeFromStorage for persistent storage for key="${key}":`, undefined, e);
      }

      resolve(true);
    } catch (error) {
      // Log error for debugging purposes
      log.error(`Error in removeFromStorage for key="${key}":`, undefined, error);
      // Reject promise with the error to allow caller to handle it
      reject(error);
    }
  });
}

/**
 * Clears all items from storage, including those not created by this app
 * @returns Promise resolving to true when cleared successfully
 */
export async function deleteAllFromStorage(): Promise<boolean> {
  return new Promise((resolve, reject) => {
    try {
      // Clear all storage items (not just those with our prefix)
      storage.clear();

      try {
        // Clear all items from persisted storage
        persistedStorage.clear();
      } catch (e) {
        // Log error for debugging purposes
        log.error(`Error in deleteAllFromStorage for persistent storage:`, undefined, e);
      }

      // Return true to indicate successful operation
      resolve(true);
    } catch (error) {
      // Log error for debugging purposes
      log.error(`Error in deleteAllFromStorage:`, undefined, error);
      // Reject promise with the error to allow caller to handle it
      reject(error);
    }
  });
}
